import { buildXlsx, type CellValue } from './xlsx'

/**
 * Exports.
 *
 * The legacy dashboard's DataTables buttons exported whatever happened to be
 * rendered, which silently dropped every row outside the current page. Here the
 * caller supplies a `fetchAll` that walks the API with the *same filters* the
 * screen is showing, so an export is the complete filtered result — or it fails
 * loudly rather than quietly writing a partial file.
 */

export interface ExportColumn<T> {
  header: string
  value: (row: T) => CellValue
}

export type ExportFormat = 'csv' | 'excel' | 'pdf' | 'print'

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.rel = 'noopener'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 2000)
}

export function timestampedFilename(base: string, extension: string): string {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(
    now.getMinutes(),
  )}`
  return `${base}-${stamp}.${extension}`
}

function toMatrix<T>(columns: ExportColumn<T>[], rows: T[]): { headers: string[]; body: CellValue[][] } {
  return {
    headers: columns.map((column) => column.header),
    body: rows.map((row) => columns.map((column) => column.value(row))),
  }
}

/* -------------------------------------------------------------------- CSV */

/**
 * RFC 4180 escaping, plus a guard against spreadsheet formula injection: a cell
 * starting with = + - or @ is executed on open by Excel and Sheets, so an
 * address or a name typed by a customer could run as a formula.
 */
function csvCell(value: CellValue): string {
  if (value === null || value === undefined) return ''

  let text = String(value)
  if (/^[=+\-@\t\r]/.test(text)) {
    text = `'${text}`
  }
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`
  }
  return text
}

export function toCsv<T>(columns: ExportColumn<T>[], rows: T[]): string {
  const { headers, body } = toMatrix(columns, rows)
  const lines = [headers.map(csvCell).join(','), ...body.map((row) => row.map(csvCell).join(','))]
  return lines.join('\r\n')
}

export function exportCsv<T>(columns: ExportColumn<T>[], rows: T[], baseName: string): void {
  // The BOM is what makes Excel read the file as UTF-8 rather than as the
  // system code page, which otherwise mangles Arabic names and addresses.
  const blob = new Blob(['﻿', toCsv(columns, rows)], { type: 'text/csv;charset=utf-8;' })
  download(blob, timestampedFilename(baseName, 'csv'))
}

/* ------------------------------------------------------------------ Excel */

export function exportExcel<T>(columns: ExportColumn<T>[], rows: T[], baseName: string, sheetName: string): void {
  const { headers, body } = toMatrix(columns, rows)
  download(buildXlsx(headers, body, sheetName), timestampedFilename(baseName, 'xlsx'))
}

/* -------------------------------------------------------------------- PDF */

export async function exportPdf<T>(
  columns: ExportColumn<T>[],
  rows: T[],
  baseName: string,
  title: string,
): Promise<void> {
  // Loaded on demand: the PDF toolchain is far larger than the rest of the
  // dashboard and most sessions never export.
  const [{ jsPDF }, autoTableModule] = await Promise.all([import('jspdf'), import('jspdf-autotable')])
  const autoTable = autoTableModule.default

  const { headers, body } = toMatrix(columns, rows)
  const document_ = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })

  document_.setFontSize(14)
  document_.text(title, 40, 40)
  document_.setFontSize(9)
  document_.setTextColor(120)
  document_.text(`${rows.length} records · exported ${new Date().toLocaleString('en-GB')}`, 40, 56)

  autoTable(document_, {
    head: [headers],
    body: body.map((row) => row.map((cell) => (cell === null || cell === undefined ? '' : String(cell)))),
    startY: 72,
    styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
    headStyles: { fillColor: [133, 4, 4], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 248, 249] },
    margin: { left: 40, right: 40 },
  })

  document_.save(timestampedFilename(baseName, 'pdf'))
}

/* ------------------------------------------------------------------ Print */

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Opens a print-ready view of the full result set.
 *
 * A separate window is used rather than printing the page, so the export
 * contains every filtered row instead of the one page on screen.
 */
export function printRows<T>(columns: ExportColumn<T>[], rows: T[], title: string): void {
  const { headers, body } = toMatrix(columns, rows)

  const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=1100,height=800')
  if (!printWindow) {
    throw new Error('The print window was blocked. Please allow pop-ups for this site and try again.')
  }

  const tableHead = headers.map((header) => `<th>${escapeHtml(header)}</th>`).join('')
  const tableBody = body
    .map(
      (row) =>
        `<tr>${row
          .map((cell) => `<td>${escapeHtml(cell === null || cell === undefined ? '' : String(cell))}</td>`)
          .join('')}</tr>`,
    )
    .join('')

  printWindow.document.write(`<!doctype html>
<html lang="en"><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>
  @page { size: A4 landscape; margin: 14mm; }
  body { font-family: 'Segoe UI', system-ui, sans-serif; color: #1d1d23; margin: 0; }
  header { border-bottom: 2px solid #850404; padding-bottom: 10px; margin-bottom: 16px; }
  h1 { font-size: 17px; margin: 0 0 4px; color: #850404; }
  p { font-size: 11px; color: #5c5c68; margin: 0; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; }
  th { text-align: left; background: #f1f1f3; border-bottom: 1.5px solid #d3d3da; padding: 6px 8px;
       text-transform: uppercase; letter-spacing: 0.06em; font-size: 9px; color: #454550; }
  td { border-bottom: 1px solid #e5e5e9; padding: 6px 8px; vertical-align: top; }
  tr { page-break-inside: avoid; }
  thead { display: table-header-group; }
</style></head>
<body>
  <header>
    <h1>${escapeHtml(title)}</h1>
    <p>Drive For Me · ${rows.length} records · ${escapeHtml(new Date().toLocaleString('en-GB'))}</p>
  </header>
  <table><thead><tr>${tableHead}</tr></thead><tbody>${tableBody}</tbody></table>
</body></html>`)

  printWindow.document.close()
  printWindow.focus()
  // Let the document lay out before the print dialog measures it.
  setTimeout(() => printWindow.print(), 250)
}

/* ------------------------------------------------------ full-dataset fetch */

export const EXPORT_PAGE_SIZE = 500
const MAX_EXPORT_ROWS = 20_000

export interface PagedFetcher<T> {
  (page: number, size: number): Promise<{ content: T[]; totalPages: number; totalElements: number }>
}

/**
 * Walks every page of the current filtered result so an export is complete.
 *
 * Capped, because a runaway export would freeze the tab; hitting the cap throws
 * rather than writing a truncated file that looks complete.
 */
export async function fetchAllPages<T>(fetcher: PagedFetcher<T>): Promise<T[]> {
  const firstPage = await fetcher(0, EXPORT_PAGE_SIZE)

  if (firstPage.totalElements > MAX_EXPORT_ROWS) {
    throw new Error(
      `This export would contain ${firstPage.totalElements.toLocaleString('en-GB')} records, which is above the ` +
        `${MAX_EXPORT_ROWS.toLocaleString('en-GB')} limit. Narrow the filters and try again.`,
    )
  }

  const rows = [...firstPage.content]
  for (let page = 1; page < firstPage.totalPages; page += 1) {
    const next = await fetcher(page, EXPORT_PAGE_SIZE)
    rows.push(...next.content)
  }
  return rows
}
